import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { 
  BookOpen, Video, MessageCircle, Award, LogOut, PlayCircle, Plus,
  FileText, Unlock, GraduationCap, Clock, Users, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import BottomNavigation from '../components/BottomNavigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StudentDashboard() {
  const navigate = useNavigate();
  const { user, logout, getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [liveClasses, setLiveClasses] = useState([]);
  const [doubts, setDoubts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDoubtDialog, setShowDoubtDialog] = useState(false);
  const [doubtForm, setDoubtForm] = useState({
    title: '',
    description: '',
    course_id: ''
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, coursesRes, classesRes, doubtsRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard`, getAuthHeader()),
        axios.get(`${API}/enrollments/my-courses`, getAuthHeader()),
        axios.get(`${API}/live-classes`, getAuthHeader()),
        axios.get(`${API}/doubts`, getAuthHeader())
      ]);

      setStats(statsRes.data);
      setCourses(coursesRes.data);
      setLiveClasses(classesRes.data);
      setDoubts(doubtsRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDoubt = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/doubts`, doubtForm, getAuthHeader());
      toast.success('Doubt submitted successfully!');
      setShowDoubtDialog(false);
      setDoubtForm({ title: '', description: '', course_id: '' });
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to submit doubt');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Quick link menu items similar to the reference image
  const quickLinks = [
    { 
      icon: BookOpen, 
      label: 'All Courses', 
      color: 'bg-indigo-100 text-indigo-600',
      onClick: () => navigate('/courses')
    },
    { 
      icon: GraduationCap, 
      label: 'My Courses', 
      color: 'bg-teal-100 text-teal-600',
      onClick: () => navigate('/courses'),
      badge: stats?.enrollments || 0
    },
    { 
      icon: Unlock, 
      label: 'Free Content', 
      color: 'bg-green-100 text-green-600',
      onClick: () => navigate('/student/resources')
    },
    { 
      icon: Award, 
      label: 'Test Series', 
      color: 'bg-purple-100 text-purple-600',
      onClick: () => navigate('/student/quiz')
    },
    { 
      icon: FileText, 
      label: 'Notes', 
      color: 'bg-amber-100 text-amber-600',
      onClick: () => navigate('/student/resources')
    },
    { 
      icon: MessageCircle, 
      label: 'Ask Doubt', 
      color: 'bg-rose-100 text-rose-600',
      onClick: () => setShowDoubtDialog(true)
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header - Mobile Friendly */}
      <header className="bg-gradient-to-r from-indigo-600 to-teal-500 text-white sticky top-0 z-40">
        <div className="px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <BookOpen className="w-7 h-7" />
              <span className="text-xl font-bold">EduFlow</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm hidden sm:block">Hi, {user?.name?.split(' ')[0]}</span>
              <Button 
                onClick={handleLogout} 
                variant="ghost" 
                size="sm" 
                className="text-white hover:bg-white/20 hidden md:flex"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-indigo-600 to-teal-500 text-white px-4 pb-8 pt-2">
        <h1 className="text-2xl font-bold mb-1">Welcome back, {user?.name?.split(' ')[0]}! 👋</h1>
        <p className="text-white/80 text-sm">Continue your learning journey</p>
        
        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-3 mt-4">
          <div className="bg-white/20 backdrop-blur rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{stats?.enrollments || 0}</p>
            <p className="text-xs text-white/80">Courses</p>
          </div>
          <div className="bg-white/20 backdrop-blur rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{stats?.tests_taken || 0}</p>
            <p className="text-xs text-white/80">Tests</p>
          </div>
          <div className="bg-white/20 backdrop-blur rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{stats?.doubts_asked || 0}</p>
            <p className="text-xs text-white/80">Doubts</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="px-4 py-6 -mt-4">
        {/* Quick Links Grid - Like Reference Image */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          {quickLinks.map((link, index) => {
            const Icon = link.icon;
            return (
              <Card
                key={index}
                data-testid={`quick-link-${link.label.toLowerCase().replace(' ', '-')}`}
                onClick={link.onClick}
                className="bg-white rounded-xl p-4 text-center cursor-pointer hover:shadow-md transition-all border border-slate-100 relative"
              >
                <div className={`w-12 h-12 mx-auto rounded-xl ${link.color} flex items-center justify-center mb-2`}>
                  <Icon className="w-6 h-6" />
                </div>
                <p className="text-xs font-medium text-slate-700 leading-tight">{link.label}</p>
                {link.badge > 0 && (
                  <span className="absolute top-2 right-2 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {link.badge}
                  </span>
                )}
              </Card>
            );
          })}
        </div>

        {/* Live Classes Section */}
        {liveClasses.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-slate-900">Live Classes</h2>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/student/live')}
                className="text-indigo-600"
              >
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            <div className="space-y-3">
              {liveClasses.slice(0, 2).map((liveClass) => (
                <Card
                  key={liveClass.id}
                  data-testid={`live-class-${liveClass.id}`}
                  className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm"
                  onClick={() => navigate(`/live-class/${liveClass.id}`)}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                      <Video className="w-6 h-6 text-red-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900 truncate">{liveClass.title}</h3>
                      <p className="text-xs text-slate-500 flex items-center mt-1">
                        <Clock className="w-3 h-3 mr-1" />
                        {new Date(liveClass.scheduled_at).toLocaleString()}
                      </p>
                    </div>
                    <Button size="sm" className="rounded-full bg-red-500 hover:bg-red-600 flex-shrink-0">
                      <PlayCircle className="w-4 h-4" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* My Courses Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-slate-900">My Courses</h2>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/courses')}
              className="text-indigo-600"
            >
              Browse <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          {courses.length === 0 ? (
            <Card className="bg-white rounded-xl p-6 text-center border border-slate-100">
              <BookOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500 text-sm">No courses enrolled yet</p>
              <Button 
                onClick={() => navigate('/courses')}
                className="mt-3 rounded-full"
                size="sm"
              >
                Explore Courses
              </Button>
            </Card>
          ) : (
            <div className="space-y-3">
              {courses.slice(0, 3).map((enrollment) => (
                <Card
                  key={enrollment.id}
                  data-testid={`course-${enrollment.id}`}
                  className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm cursor-pointer hover:shadow-md transition-all"
                  onClick={() => navigate(`/course/${enrollment.course_id}`)}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900 truncate">{enrollment.course_title}</h3>
                      <div className="flex items-center mt-2">
                        <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-teal-500 rounded-full"
                            style={{ width: `${enrollment.progress}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-slate-500 ml-2">{enrollment.progress}%</span>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Recent Doubts */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-slate-900">My Doubts</h2>
            <Dialog open={showDoubtDialog} onOpenChange={setShowDoubtDialog}>
              <DialogTrigger asChild>
                <Button size="sm" className="rounded-full bg-indigo-600">
                  <Plus className="w-4 h-4 mr-1" />
                  Ask
                </Button>
              </DialogTrigger>
              <DialogContent className="mx-4 rounded-xl">
                <DialogHeader>
                  <DialogTitle className="text-slate-900">Ask a Doubt</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateDoubt} className="space-y-4">
                  <div>
                    <Label htmlFor="doubt-title" className="text-slate-700">Title</Label>
                    <Input
                      id="doubt-title"
                      data-testid="doubt-title-input"
                      value={doubtForm.title}
                      onChange={(e) => setDoubtForm({ ...doubtForm, title: e.target.value })}
                      placeholder="Brief title for your doubt"
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="doubt-description" className="text-slate-700">Description</Label>
                    <Textarea
                      id="doubt-description"
                      data-testid="doubt-description-input"
                      value={doubtForm.description}
                      onChange={(e) => setDoubtForm({ ...doubtForm, description: e.target.value })}
                      placeholder="Explain your doubt in detail..."
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="doubt-course" className="text-slate-700">Related Course (Optional)</Label>
                    <Select
                      value={doubtForm.course_id}
                      onValueChange={(value) => setDoubtForm({ ...doubtForm, course_id: value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select a course" />
                      </SelectTrigger>
                      <SelectContent>
                        {courses.map((enrollment) => (
                          <SelectItem key={enrollment.course_id} value={enrollment.course_id}>
                            {enrollment.course_title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="w-full rounded-full bg-indigo-600">
                    Submit Doubt
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          {doubts.length === 0 ? (
            <Card className="bg-white rounded-xl p-6 text-center border border-slate-100">
              <MessageCircle className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500 text-sm">No doubts asked yet</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {doubts.slice(0, 2).map((doubt) => (
                <Card
                  key={doubt.id}
                  data-testid={`doubt-${doubt.id}`}
                  className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900">{doubt.title}</h3>
                      <p className="text-sm text-slate-600 mt-1 line-clamp-2">{doubt.description}</p>
                      {doubt.answer && (
                        <div className="mt-2 bg-teal-50 border border-teal-200 rounded-lg p-2">
                          <p className="text-xs font-medium text-teal-900">Answer:</p>
                          <p className="text-xs text-teal-700 mt-0.5 line-clamp-2">{doubt.answer}</p>
                        </div>
                      )}
                    </div>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ml-2 ${
                        doubt.status === 'answered'
                          ? 'bg-teal-100 text-teal-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}
                    >
                      {doubt.status}
                    </span>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Bottom Navigation */}
      <BottomNavigation />
    </div>
  );
}
